"use client";

import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Global navigation progress bar — a thin line pinned to the top of the app,
 * in the vein of YouTube's.
 *
 * Timing is the whole point: it starts on the *click*, not on the route
 * change. App Router navigations run as a React transition that only commits
 * once the router has a renderable tree (from the prefetch cache or the RSC
 * response), and `usePathname()` doesn't change until then — so anything
 * driven by route changes only appears after the wait it was meant to cover.
 * Intercepting the click in the capture phase gets the bar on screen on the
 * same frame, whether or not the destination was prefetched.
 *
 * Motion is a per-frame exponential ease toward a ceiling it never reaches:
 * fast off the line, continuously decelerating, so a slow navigation keeps
 * showing movement without ever implying it's about to finish. The bar is
 * driven imperatively through a ref — writing `transform` on each frame keeps
 * the animation on the compositor and avoids a React render per frame (and
 * `transform` rather than `width`, which would relayout 60 times a second).
 */

/** Asymptote, in percent. Only completion reaches 100. */
const CREEP_CEILING = 90;
/** Where it jumps to on the first frame, so the click reads instantly. */
const CREEP_FLOOR = 6;
/** Time constant: ~63% of the remaining distance is covered per interval. */
const CREEP_TIME_CONSTANT_MS = 1400;
/**
 * Nothing legitimately takes this long; releases a stuck bar. Shared with the
 * sidebar's pending-entry state so the two pieces of navigation feedback can't
 * disagree about when a navigation has failed.
 */
export const NAVIGATION_STUCK_TIMEOUT_MS = 20_000;
const COMPLETE_DURATION_MS = 380;

export function NavigationProgress() {
  const pathname = usePathname();
  const [active, setActive] = useState(false);
  const [completing, setCompleting] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);
  const previousPathnameRef = useRef(pathname);

  const start = useCallback(() => {
    setCompleting(false);
    setActive(true);
  }, []);

  // Creep, one frame at a time. `1 - e^(-t/τ)` is smooth and self-damping:
  // no steps to interpolate between, so there's nothing to stutter.
  useEffect(() => {
    if (!active || completing) return;
    const bar = barRef.current;
    if (!bar) return;

    bar.style.transition = "none";
    bar.style.opacity = "1";

    const startedAt = performance.now();
    let frame = requestAnimationFrame(function tick(now) {
      const elapsed = now - startedAt;
      const value =
        CREEP_FLOOR +
        (CREEP_CEILING - CREEP_FLOOR) *
          (1 - Math.exp(-elapsed / CREEP_TIME_CONSTANT_MS));
      bar.style.transform = `scaleX(${value / 100})`;
      frame = requestAnimationFrame(tick);
    });

    return () => cancelAnimationFrame(frame);
  }, [active, completing]);

  // Run out to the end, then fade. The fade is delayed just past the fill so
  // the bar is seen arriving rather than vanishing mid-travel.
  useEffect(() => {
    if (!completing) return;
    const bar = barRef.current;
    if (bar) {
      bar.style.transition =
        "transform 200ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease-out 160ms";
      bar.style.transform = "scaleX(1)";
      bar.style.opacity = "0";
    }
    const timeout = setTimeout(() => {
      setActive(false);
      setCompleting(false);
    }, COMPLETE_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [completing]);

  // Complete once the transition commits — that's when the new route (its
  // skeleton or its content) is actually on screen.
  useEffect(() => {
    if (previousPathnameRef.current === pathname) return;
    previousPathnameRef.current = pathname;
    if (!active) return;
    setCompleting(true);
  }, [pathname, active]);

  // A navigation can be cancelled or fail without the pathname ever
  // changing, which would strand the bar mid-creep.
  useEffect(() => {
    if (!active || completing) return;
    const timeout = setTimeout(
      () => setCompleting(true),
      NAVIGATION_STUCK_TIMEOUT_MS,
    );
    return () => clearTimeout(timeout);
  }, [active, completing]);

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (event.defaultPrevented || event.button !== 0) return;
      // Modified clicks open a new tab: this document never navigates.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      if (!(event.target instanceof Element)) return;

      const anchor = event.target.closest("a");
      if (!anchor || anchor.hasAttribute("download")) return;
      if (anchor.target && anchor.target !== "_self") return;

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#")) return;

      // `anchor.href` is already resolved against the document, so this
      // handles relative hrefs without any manual joining.
      let destination: URL;
      try {
        destination = new URL(anchor.href);
      } catch {
        return;
      }
      if (destination.origin !== window.location.origin) return;
      // Same-pathname links (query-only changes, or the route you're already
      // on) never fire the completion effect, so they'd strand the bar.
      if (destination.pathname === window.location.pathname) return;

      start();
    }

    // Capture phase: React's own click handling can stop propagation, and
    // this needs to run regardless.
    document.addEventListener("click", handleClick, { capture: true });
    return () => {
      document.removeEventListener("click", handleClick, { capture: true });
    };
  }, [start]);

  if (!active) return null;

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-0 z-[200] h-[3px]"
    >
      <div
        ref={barRef}
        // The resting state has to be set through `transform`, the same
        // channel the frame loop writes. Tailwind v4's `scale-x-0` emits the
        // standalone `scale` property, which composes with `transform`
        // rather than being overridden by it — it would pin the bar at zero
        // width for the whole animation.
        style={{ transformOrigin: "left", transform: "scaleX(0)" }}
        className="h-full w-full bg-nf-ds-purple-product shadow-[0_0_8px_0_var(--nf-ds-purple-product)] will-change-transform"
      />
    </div>
  );
}
